/* components/LooomCanvas.svelte generated by Svelte v3.32.1 */
import {
	SvelteComponent,
	append,
	attr,
	binding_callbacks,
	detach,
	element,
	init,
	insert,
	noop,
	safe_not_equal
} from "svelte/internal";

import parseLooom from "../../src/parse-looom-svg";
import { createRenderer } from "../../src/canvas-rendering";
import { createEventDispatcher, onMount } from "svelte";
import objectFit from "../../src/util/fit";
import createRecording from "./record";
import Color from "canvas-sketch-util/color";
import rightNow from "right-now";

function create_fragment(ctx) {
	let div;
	let canvas_1;

	return {
		c() {
			div = element("div");
			canvas_1 = element("canvas");
			attr(div, "class", "looom-container svelte-16p6p79");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, canvas_1);
			/*canvas_1_binding*/ ctx[23](canvas_1);
		},
		p: noop,
		i: noop,
		o: noop,
		d(detaching) {
			if (detaching) detach(div);
			/*canvas_1_binding*/ ctx[23](null);
		}
	};
}

function downloadBlob(buf, filename, type) {
	const blob = buf instanceof Blob ? buf : new Blob([buf], { type });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;
	anchor.click();
}

function instance($$self, $$props, $$invalidate) {
	const MAX_SIZE = 4096 * 4;
	let { data = null } = $$props;
	let { recenter = false } = $$props;
	let { refit = true } = $$props;
	let { resamplePaths = false } = $$props;
	let { transparentBackground = false } = $$props;
	let { fit = "contain" } = $$props;
	let { fitScale = 1 } = $$props;
	let { fitX = 0.5 } = $$props;
	let { fitY = 0.5 } = $$props;
	let { running = true } = $$props;
	let { recording = false } = $$props;
	let { sizing = "weave" } = $$props;
	let { customWidth = 1024 } = $$props;
	let { customHeight = 768 } = $$props;
	let { format = "mp4" } = $$props;
	let { fps = 30 } = $$props;
	let { duration = 5 } = $$props;
	let { qualityPreset = "high" } = $$props;
	let { quantizeWithAlpha = true } = $$props;
	let { time = 0 } = $$props;
	let { scaleToView = true } = $$props;
	const dispatcher = createEventDispatcher();
	let width, height, pixelRatio;
	let weave;
	let renderWeave;
	let canvas, context;
	let raf;
	let lastTime;
	let _isRunning = false;
	let _isRecording = false;
	let currentRecorder;
	let frameElapsed;
	let frameLastTime;

	function downloadJSON() {
		if (!weave) {
			return alert("Drop a valid Looom file to download it");
		}

		const text = JSON.stringify(weave, undefined, 2);
		const type = "application/json";
		const blob = new Blob([text], { type });
		downloadBlob(blob, "weave.json", { type });
	}

	function reparse(data) {
		$$invalidate(2, time = 0);

		if (data) {
			try {
				try {
					weave = parseLooom(data, { recenter });
				} catch(err) {
					console.error(err);

					// mask huge SVG error
					throw new Error(`Could not parse SVG data from file`);
				}

				/*
if (weave.version !== "2") {
  throw new Error(
    `Only supports weave version 2 files, got ${weave.version}`
  );
}
*/
				renderWeave = createRenderer(weave, {
					refit,
					resamplePaths,
					fit: {
						fit,
						scale: fitScale,
						offsetX: fitX,
						offsetY: fitY
					}
				});
			} catch(err) {
				console.error(err);
				window.alert(err.message);
				weave = renderWeave = null;
			}
		} else {
			weave = renderWeave = null;
		}

		if (canvas) resize();
		dispatcher("load", weave);
	}

	onMount(() => {
		context = canvas.getContext("2d");
		resize();
		window.addEventListener("resize", resize, { passive: true });
		window.addEventListener("orientationchange", resize, { passive: true });

		return () => {
			window.removeEventListener("resize", resize);
			window.removeEventListener("orientationchange", resize);
			stopLoop();
		};
	});

	function getKnownColors() {
		if (!weave) return [];
		const set = new Set();
		if (!transparentBackground) set.add(weave.backgroundColor);

		weave.threads.forEach(t => {
			const opt = t.options;
			if (!opt.visible) return;

			if (opt.stroke && opt.strokeOpacity >= 1) {
				set.add(opt.stroke);
			}

			if (opt.fill && opt.fillOpacity >= 1) {
				set.add(opt.fill);
			}
		});

		return [...set].map(hex => Color.parse(hex).rgb);
	}

	function startRecord() {
		if (_isRecording) return;
		_isRecording = true;
		resize(); // stop scaling to view
		redraw(); // force redraw so we don't see any missed frames

		// console.log("start record");
		const w = parseInt(canvas.width, 10);

		const h = parseInt(canvas.height, 10);
		let curTime = 0;
		dispatcher("recordStart");

		const formats = {
			mp4: { ext: ".mp4", type: "video/mp4" },
			gif: { ext: ".gif", type: "image/gif" },
			webm: { ext: ".webm", type: "video/webm" },
			"sequence:png": {
				ext: ".png",
				type: "image/png",
				sequence: true
			},
			"sequence:svg": {
				ext: ".svg",
				type: "image/svg+xml",
				sequence: true
			}
		};

		const { ext, type, sequence } = formats[format];

		const drawFrame = ({ deltaTime, frame, totalFrames }) => {
			curTime += deltaTime;
			redrawWithTime(curTime);

			if (sequence) {
				if (ext === ".svg") {
					return {
						extension: ext,
						type,
						data: `<svg><text>${frame}</text></svg>`
					};
				} else {
					return {
						extension: ext,
						type,
						url: canvas.toDataURL(type)
					};
				}
			} else {
				const img = context.getImageData(0, 0, w, h);
				return img;
			}
		};

		const filename = `animation${ext}`;
		let startTime;
		const totalFrames = Math.ceil(fps * duration);

		currentRecorder = createRecording(canvas, drawFrame, {
			progress(opts) {
				dispatcher("recordProgress", opts);
			},
			backgroundColor: Color.parse(weave.backgroundColor || "#000").rgb,
			quantizeWithAlpha,
			transparent: transparentBackground,
			knownColors: getKnownColors(),
			width: w,
			height: h,
			duration,
			fps,
			format: sequence ? "sequence" : format,
			qualityPreset,
			start() {
				startTime = rightNow();
			},
			finish() {
				const endTime = rightNow();
				const elapsed = endTime - startTime;
				console.log("Finished in", elapsed, "ms");
				console.log("mspf", elapsed / totalFrames);
			}
		});

		currentRecorder.ready.then(buf => {
			if (buf === null) {
				console.log("Recording cancelled");
				dispatcher("recordCancel");
			} else {
				dispatcher("recordSuccess");

				if (buf && !sequence) {
					downloadBlob(buf, filename, type);
				}
			}

			resize(); // scale back to view
			_isRecording = false;
			$$invalidate(1, recording = false);
			currentRecorder = null;
			dispatcher("recordFinish");
		});
	}

	function stopRecord() {
		if (!_isRecording) return;

		// console.log("stop record");
		if (currentRecorder) {
			currentRecorder.cancel();
			currentRecorder = null;
		}

		_isRecording = false;
	}

	function startLoop() {
		if (_isRunning) return;
		const now = rightNow();
		lastTime = now;
		frameLastTime = now;
		raf = window.requestAnimationFrame(animate);
		_isRunning = true;
	}

	function stopLoop() {
		_isRunning = false;

		if (raf != null) {
			window.cancelAnimationFrame(raf);
			raf = null;
		}
	}

	function animate() {
		raf = window.requestAnimationFrame(animate);
		let now = rightNow();
		const dt = (now - lastTime) / 1000;

		if (running && _isRunning) {
			$$invalidate(2, time += dt);
		}

		let newFrame = false;
		const fpsInterval = 1000 / fps;
		frameElapsed = now - frameLastTime;

		if (frameElapsed > fpsInterval) {
			frameLastTime = now - frameElapsed % fpsInterval;
			newFrame = true;
		}

		// don't throttle any frames at 60+
		if (fps >= 60) newFrame = true;

		if (!_isRecording && newFrame) {
			redraw();
		}

		lastTime = now;
	}

	function redraw() {
		// time = 10 / fps;
		redrawWithTime(time);
	}

	function redrawWithTime(curTime = 0) {
		context.save();
		context.scale(pixelRatio, pixelRatio);

		// context.clearRect(0, 0, width, height);
		// context.fillStyle = "blue";
		// context.fillRect(0, 0, width, height);
		if (renderWeave) {
			let showBackground = !transparentBackground;

			if (format === "gif" && !quantizeWithAlpha) {
				// If we wish to quantize with just RGB channel,
				// then we can turn the background back on and alpha-cut it later
				showBackground = true;
			}

			renderWeave(context, {
				width,
				height,
				time: curTime,
				background: showBackground
			});
		}

		context.restore();
	}

	function userSize(n) {
		if (isFinite(n) && n >= 2 && n <= MAX_SIZE) return n;
		return 1024; // default size
	}

	function resize() {
		if (!canvas) return;
		const innerWidth = window.innerWidth;
		const innerHeight = window.innerHeight;
		let tx = 0;
		let ty = 0;
		let styleWidth, styleHeight;

		if (sizing === "window") {
			pixelRatio = Math.min(2, window.devicePixelRatio || 1);
			width = innerWidth;
			height = innerHeight;
			styleWidth = width;
			styleHeight = height;
		} else {
			pixelRatio = 1;

			if (sizing === "weave" && weave) {
				width = weave.width;
				height = weave.height;
			} else {
				width = userSize(customWidth);
				height = userSize(customHeight);
			}

			const fitted = objectFit({
				parentWidth: innerWidth,
				parentHeight: innerHeight,
				fit: "contain",
				scale: 0.8,
				childWidth: width,
				childHeight: height
			});

			tx = fitted[0];
			ty = fitted[1];
			styleWidth = fitted[2];
			styleHeight = fitted[3];
		}

		// If we aren't recording and we are supposed to scale
		// to the browser view for faster rendering, let's do that here
		if (scaleToView && !_isRecording) {
			width = styleWidth;
			height = styleHeight;
			pixelRatio = Math.min(2, window.devicePixelRatio);
		}

		const canvasWidth = Math.floor(width * pixelRatio);
		const canvasHeight = Math.floor(height * pixelRatio);
		$$invalidate(0, canvas.width = canvasWidth, canvas);
		$$invalidate(0, canvas.height = canvasHeight, canvas);
		$$invalidate(0, canvas.style.position = `relative`, canvas);
		$$invalidate(0, canvas.style.left = `${tx}px`, canvas);
		$$invalidate(0, canvas.style.top = `${ty}px`, canvas);
		$$invalidate(0, canvas.style.width = `${styleWidth}px`, canvas);
		$$invalidate(0, canvas.style.height = `${styleHeight}px`, canvas);
		if (!_isRecording) redraw();
	}

	function canvas_1_binding($$value) {
		binding_callbacks[$$value ? "unshift" : "push"](() => {
			canvas = $$value;
			$$invalidate(0, canvas);
		});
	}

	$$self.$$set = $$props => {
		if ("data" in $$props) $$invalidate(3, data = $$props.data);
		if ("recenter" in $$props) $$invalidate(4, recenter = $$props.recenter);
		if ("refit" in $$props) $$invalidate(5, refit = $$props.refit);
		if ("resamplePaths" in $$props) $$invalidate(6, resamplePaths = $$props.resamplePaths);
		if ("transparentBackground" in $$props) $$invalidate(7, transparentBackground = $$props.transparentBackground);
		if ("fit" in $$props) $$invalidate(8, fit = $$props.fit);
		if ("fitScale" in $$props) $$invalidate(9, fitScale = $$props.fitScale);
		if ("fitX" in $$props) $$invalidate(10, fitX = $$props.fitX);
		if ("fitY" in $$props) $$invalidate(11, fitY = $$props.fitY);
		if ("running" in $$props) $$invalidate(12, running = $$props.running);
		if ("recording" in $$props) $$invalidate(1, recording = $$props.recording);
		if ("sizing" in $$props) $$invalidate(13, sizing = $$props.sizing);
		if ("customWidth" in $$props) $$invalidate(14, customWidth = $$props.customWidth);
		if ("customHeight" in $$props) $$invalidate(15, customHeight = $$props.customHeight);
		if ("format" in $$props) $$invalidate(16, format = $$props.format);
		if ("fps" in $$props) $$invalidate(17, fps = $$props.fps);
		if ("duration" in $$props) $$invalidate(18, duration = $$props.duration);
		if ("qualityPreset" in $$props) $$invalidate(19, qualityPreset = $$props.qualityPreset);
		if ("quantizeWithAlpha" in $$props) $$invalidate(20, quantizeWithAlpha = $$props.quantizeWithAlpha);
		if ("time" in $$props) $$invalidate(2, time = $$props.time);
		if ("scaleToView" in $$props) $$invalidate(21, scaleToView = $$props.scaleToView);
	};

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*recenter, resamplePaths, refit, fitX, fitY, fitScale, fit, data*/ 3960) {
			$: (recenter, resamplePaths, refit, fitX, fitY, fitScale, fit, reparse(data));
		}

		if ($$self.$$.dirty[0] & /*sizing, customWidth, customHeight*/ 57344) {
			$: (sizing, customWidth, customHeight, resize());
		}

		if ($$self.$$.dirty[0] & /*recording, running*/ 4098) {
			$: {
				if (recording) {
					stopLoop();
				} else {
					if (running) startLoop(); else stopLoop();
				}

				if (recording) startRecord(); else stopRecord();
			}
		}
	};

	return [
		canvas,
		recording,
		time,
		data,
		recenter,
		refit,
		resamplePaths,
		transparentBackground,
		fit,
		fitScale,
		fitX,
		fitY,
		running,
		sizing,
		customWidth,
		customHeight,
		format,
		fps,
		duration,
		qualityPreset,
		quantizeWithAlpha,
		scaleToView,
		downloadJSON,
		canvas_1_binding
	];
}

class LooomCanvas extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance,
			create_fragment,
			safe_not_equal,
			{
				data: 3,
				recenter: 4,
				refit: 5,
				resamplePaths: 6,
				transparentBackground: 7,
				fit: 8,
				fitScale: 9,
				fitX: 10,
				fitY: 11,
				running: 12,
				recording: 1,
				sizing: 13,
				customWidth: 14,
				customHeight: 15,
				format: 16,
				fps: 17,
				duration: 18,
				qualityPreset: 19,
				quantizeWithAlpha: 20,
				time: 2,
				scaleToView: 21,
				downloadJSON: 22
			},
			[-1, -1]
		);
	}

	get downloadJSON() {
		return this.$$.ctx[22];
	}
}

export default LooomCanvas;